"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.createStaticApp = createStaticApp;
var _cache = _interopRequireDefault(require("@emotion/cache"));
var _v14PagesRouter = require("@mui/material-nextjs/v14-pagesRouter");
var _mui = require("@ori-ui/mui");
var _logger = require("@ori/logger");
var _zodUtils = require("@ori/zod-utils");
var _app = _interopRequireDefault(require("next/app"));
var _constants = require("next/constants");
var _react = require("react");
var _AxeAccessibilityChecker = require("./AxeAccessibilityChecker");
var _jsxRuntime = require("react/jsx-runtime");
/* eslint-disable  no-param-reassign -- okay for this case */

// here must be direct import, otherwise it will try to parse initialize.ts on client

function createStaticApp({
  accessibilityGuidelinesTags,
  emotionCacheKey,
  appName
}) {
  const StaticApp = ({
    Component,
    pageProps,
    loggerMetadata,
    applicationInsightsClientTags,
    emotionCache = (0, _cache.default)({
      key: emotionCacheKey ?? appName
    }),
    loggerConfiguration
  }) => {
    const cloudRoleInstanceHandler = (0, _react.useRef)(telemetryItem => {
      telemetryItem.tags ??= {};
      telemetryItem.tags = {
        ...telemetryItem.tags,
        ...applicationInsightsClientTags
      };
    });
    if (typeof window !== 'undefined') {
      // IMPORTANT: Do any modifications to logger before the first log starts
      if (loggerConfiguration) {
        _logger.Logger.setConfiguration(loggerConfiguration);
      }
      if (loggerMetadata) {
        // Its okay if it is called multiple times
        _logger.Logger.updateGlobalMetadata(loggerMetadata);
      }

      // Try to initialize on client
      if (global.oriAppInsightsSettings && _logger.Logger.isTransportConfigured(_logger.LoggerTransportType.APPLICATION_INSIGHTS) && !_logger.ApplicationInsights.isInitialized()) {
        // Its okay if it is called multiple times
        _logger.ApplicationInsights.initialize(global.oriAppInsightsSettings);

        // The init may fail so do not modify it
        if (_logger.ApplicationInsights.isInitialized()) {
          _logger.ApplicationInsights.getForClient().addTelemetryInitializer(cloudRoleInstanceHandler.current);
          _logger.Logger.trackPageView();
        }
      }
    }
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_logger.AppInsightsProvider, {
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_mui.ThemeProvider, {
        children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(_v14PagesRouter.AppCacheProvider, {
          emotionCache: emotionCache,
          children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_mui.CssBaseline, {}), /*#__PURE__*/(0, _jsxRuntime.jsx)(_AxeAccessibilityChecker.AxeAccessibilityChecker, {
            accessibilityGuidelinesTags: accessibilityGuidelinesTags
          }), /*#__PURE__*/(0, _jsxRuntime.jsx)(Component, {
            ...pageProps
          })]
        })
      })
    });
  };
  StaticApp.getInitialProps = async ctx => {
    const initialProps = await _app.default.getInitialProps(ctx);
    let loggerMetadata = null;
    let loggerConfiguration = null;
    const applicationInsightsClientTags = {};
    const isProduction = process.env.NODE_ENV === 'production';
    const isNextBuild = process.env.NEXT_PHASE === _constants.PHASE_PRODUCTION_BUILD;
    if (typeof window === 'undefined') {
      const {
        appConfig
      } = await import(/* webpackIgnore: true */'@ori-appshell/config/server');
      const tenant = (0, _zodUtils.validateTenant)(ctx.router.query.tenant);
      const language = (0, _zodUtils.validateLanguage)(ctx.router.query.language);
      loggerMetadata = {
        tenant,
        language
      };
      loggerConfiguration = _logger.Logger.getConfiguration();

      // Only in production and out of build
      if (isProduction && !isNextBuild) {
        const {
          loadDevelopmentInfo
        } = await import(/* webpackIgnore: true */'@ori-appshell/routing');
        const developmentInfo = await loadDevelopmentInfo();
        loggerMetadata.version = developmentInfo.version;
        loggerMetadata.environment = appConfig.getConfig().ENVIRONMENT;

        // Application Insights was not initialized in some weird cases, this lets it initialize itself again
        if (!_logger.ApplicationInsights.isInitialized() && global.oriAppInsightsSettings) {
          _logger.ApplicationInsights.initialize(global.oriAppInsightsSettings);
        }
        if (_logger.ApplicationInsights.isInitialized()) {
          const {
            keys,
            tags
          } = _logger.ApplicationInsights.getForServer().defaultClient.context;
          const cloudRoleInstance = tags[keys.cloudRoleInstance];
          if (cloudRoleInstance) {
            applicationInsightsClientTags[keys.cloudRoleInstance] = cloudRoleInstance;
          }
        }
      }

      // Set metadata for logger globally on server
      _logger.Logger.updateGlobalMetadata(loggerMetadata);
    }
    return {
      ...initialProps,
      loggerMetadata,
      applicationInsightsClientTags,
      loggerConfiguration
    };
  };
  return StaticApp;
}
